import { DateTime } from 'luxon'
import { computed, nextTick, type Ref } from 'vue'
import { useCalendarStore } from '@/features/slicers/useCalendarStore'
import { useTelemetry } from '@/features/telemetry/useTelemetry'
import { type Store } from '@/types'

export function useStoreDateRanges(storeRef: Ref<Store>) {
	const {
		getSelectedDate,
		getVisibleRangeStartDate,
		getVisibleRangeEndDate,
		canMoveBack: canMoveBackPerStore,
		canMoveForward: canMoveForwardPerStore,
		setSelectedDate: setSelectedDatePerStore
	} = useCalendarStore()
	const { trackEvent } = useTelemetry()

	const selectedDate = computed(() => getSelectedDate(storeRef.value.id))
	const visibleRangeStartDate = computed(() => getVisibleRangeStartDate(storeRef.value.id))
	const visibleRangeEndDate = computed(() => getVisibleRangeEndDate(storeRef.value.id))
	const canMoveBack = computed(() => canMoveBackPerStore(storeRef.value.id))
	const canMoveForward = computed(() => canMoveForwardPerStore(storeRef.value.id))

	async function goToToday() {
		const today = DateTime.now().toUTC().startOf('day')

		trackEvent('user', 'Clicked "today" button', {
			selectedDate: today.toISO(),
			store: storeRef.value.id
		})

		// A small hack to force the timeline to actually scroll to current date if it is scrolled away from it
		setSelectedDate(today.minus({ day: 1 }))

		await nextTick()

		setSelectedDate(today, true)
	}

	function setSelectedDate(date: DateTime<true>, includeRangeUpdate?: boolean) {
		setSelectedDatePerStore(storeRef.value.id, date, includeRangeUpdate)
	}

	return {
		visibleRangeStartDate,
		visibleRangeEndDate,
		selectedDate,
		canMoveBack,
		canMoveForward,
		goToToday,
		setSelectedDate
	}
}
